/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import * as React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Notification from './components/Notification';
import SignIn from './components/SignIn';
import Layout from './components/Layout';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import UserAdmin from './components/UserAdmin';
import UserEdit from './components/UserEdit';
import withNetworkDetector from './components/NetworkDetector';
import Authorization from './components/Authorization';
import SigninCallBack from './components/Signin-CallBack';
import License from './components/License';
// import { loadFromLocalStorage } from "./common/localStorage";
import { dashboardRoute, signInRoute, licenseRoute, authorizationRoute, useradminRoute, editUserRoute, GSInfoRoute, signincallbackRoute, notificationRoute, manualRoute, bulletinRoute, laborTimeGuideRoute, downloadsRoute, instrumentConstantsRoute } from './common/constants';
import GeneralServiceInformation from './components/GeneralServiceInformation';
import MyTrainingAuthorizations from './components/MyTrainingAuthorizations';
import TeamTrainingAuthorizations from './components/TeamTrainingAuthorizations';
import AllTrainingAuthorizations from './components/AllTrainingAuthorizations';
import TeamProductAuthorizations from './components/TeamProductAuthorizations';
import AllProductAuthorizations from './components/AllProductAuthorizations';
import ServiceManual from './components/ServiceManual';
import ServiceBulletin from './components/ServiceBulletin';
import LaborTimeGuide from './components/LaborTimeGuide';
import Downloads from './components/Downloads';
import InstrumentConstants from './components/InstrumentConstants';

    const currentUrl = window.location.pathname + window.location.search;
    sessionStorage.setItem('redirectAfterLogin', currentUrl);


export const theme = createTheme({
    palette: {
        primary: {
            light: '#36454f',
            main: '#36454f',
            dark: '#36454f'
        },
        secondary: {
            light: '#ffbe00',
            main: '#ffbe00',
            dark: '#ffbe00'
        },
        background: {
            default: '#f3f3f7'
        }
    },
    components: {
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    background: '#f3f3f7',
                    '& *': { color: '#FFFFFF' },
                },
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid #d6d6d6',
                    textTransform: 'capitalize',
                    textAlign: 'center',
                    color: '#4a4a4a',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 300
                },
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'capitalize'
                }
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        outline: 'none'
                    },
                    '&:click': {
                        outline: 'none'
                    },
                    '&:focus': {
                        outline: 'none'
                    },
                }
            }
        },
        MuiImageList: {
            styleOverrides: {
                root: {
                    overflowY: 'hidden',
                    overflowX: 'auto'
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height:'4px !important'
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize:'19px'
                }
            }
        },
        MuiInput: {
            styleOverrides: {
                input: {
                    fontSize:'12px'
                }
            }
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    transform: 'scale(1) translate(-20%, -35%)',
                    color: '#fff',
                    backgroundColor:'#e82e15'
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paperWidthSm: {
                    maxWidth:'none'
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    '&.Mui-checked': {
                        color: '#ffbe00'
                    }
                }
            }
        },
    }
});


class App extends React.Component<{}, {}> {
    render() {
        const AppWithNetwork = withNetworkDetector(() => (
            <Routes>
                <Route path="/" element={<Navigate to={dashboardRoute} replace />} />
                <Route path={signInRoute} element={<SignIn />} />
                <Route path="/app/license" element={<Navigate to={licenseRoute} replace />} />
                <Route path="/app/authorization" element={<Navigate to={authorizationRoute} replace />} />
                <Route path="/app/notification" element={<Navigate to={notificationRoute} replace />} />

                <Route path={`${dashboardRoute}/*`} element={<Layout><Dashboard /></Layout>} />
                <Route path={`${notificationRoute}/*`} element={<Layout><Notification /></Layout>} />
                <Route path={`${useradminRoute}/*`} element={<Layout><UserAdmin /></Layout>} />
                    <Route path={`${licenseRoute}/*`} element={<Layout><License /></Layout>} />
                    <Route path={`${GSInfoRoute}/*`} element={<Layout><GeneralServiceInformation /></Layout>} />
                    <Route path={`${manualRoute}/*`} element={<Layout><ServiceManual /></Layout>} />
                    <Route path={`${bulletinRoute}/*`} element={<Layout><ServiceBulletin /></Layout>} />
                    <Route path={`${laborTimeGuideRoute}/*`} element={<Layout><LaborTimeGuide /></Layout>} />
                    <Route path={`${downloadsRoute}/*`} element={<Layout><Downloads /></Layout>} />
                    <Route path={`${instrumentConstantsRoute}/*`} element={<Layout><InstrumentConstants /></Layout>} />
                    <Route path={`${authorizationRoute}/*`} element={<Layout><Authorization /></Layout>} >
                        <Route index element={<MyTrainingAuthorizations />} />
                        <Route path="teamtraining" element={<TeamTrainingAuthorizations />} />
                        <Route path="alltraining" element={<AllTrainingAuthorizations />} />
                        <Route path="teamproduct" element={<TeamProductAuthorizations />} />
                        <Route path="allproduct" element={<AllProductAuthorizations />} />
                    </Route>
                <Route path={signincallbackRoute} element={<SigninCallBack />} />

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        ));

        return (
            <ThemeProvider theme={theme}>
                <AppWithNetwork />
            </ThemeProvider>
        );
    }
}

export default App;
